<template>
  <div>
    <div v-if="isFetching">
      <div class="t-loader">
        <span class="loader-label">
          Fetching page <span>{{ pageNum }}</span>
        </span>
        <span class="loader-spinner"></span>
      </div>
    </div>

    <div v-else-if="imageSrc">
      <Zoomable
        ref="zoomable"
        :containerHeight="viewerHeight"
        :containerWidth="viewerWidth"
        :contentHeight="contentHeight"
        :contentWidth="contentWidth"
      >
        <PageImage
          :src="imageSrc"
          :width="contentWidth"
          alt=""
          role="presentation"
          aria-hidden="true"
          @loaded="loaded"
          @denied="denied"
        />

        <PageText
          v-if="!offScreen"
          v-bind="{extractedTextUrl, pageNum, searchTerms}"
        />

        <div v-if="!offScreen">
          <template v-for="(link, i) in imageLinks">
            <PageLink
              v-for="(rect, j) in link.rectangleList"
              :key="`${i}.${j}`"
              :top="rect.top"
              :left="rect.left"
              :height="rect.height"
              :width="rect.width"
              v-bind="link"
            />
          </template>
        </div>
      </Zoomable>
    </div>

    <div v-else>Unknown page type</div>
  </div>
</template>

<script>
import PageImage from './PageImage.vue'
import PageLink from './PageLink.vue'
import PageText from './PageText.vue'
import Zoomable from './Zoomable.vue'

export default {
  name: 'Page',

  props: {
    extractedTextUrl: {type: String, default: null},
    imageHeight: {type: Number, default: null},
    imageLinks: {type: Array, default: () => []},
    imageSrc: {type: String, default: null},
    imageWidth: {type: Number, default: null},
    isFetching: {type: Boolean, default: false},
    offScreen: {type: Boolean, required: true},
    pageNum: {type: Number, required: true},
    requestWidth: {type: Number, required: true},
    searchTerms: {type: String, required: true},
    viewerHeight: {type: Number, required: true},
    viewerWidth: {type: Number, required: true},
  },

  computed: {
    contentHeight() {
      // Until the image is loaded, we can only guess
      return this.imageHeight || this.requestWidth * 2
    },

    contentWidth() {
      // Until the image is loaded, we can only guess
      return this.imageWidth || this.requestWidth
    },
  },

  // Don't populate unused props as div attributes.
  inheritAttrs: false,

  methods: {
    // If a page image is denied, it will emit the 'denied' event which
    // is handled here. We don't immediately publish the event, because
    // it might be for a prefetched page rather than the current one.
    denied(e) {
      this.setPage(this.pageNum, {
        denied: {e},
      })
    },

    // Before the image is loaded by the browser, we only know the
    // width requested in the URL. The actual width returned by the
    // server might be different, furthermore we have no idea what the
    // image height will be until it arrives. The PageImage component
    // emits a 'loaded' event so we can update our widths and heights
    // to match the actual image.
    loaded(img) {
      this.setPage(this.pageNum, {
        imageHeight: img.naturalHeight,
        imageWidth: img.naturalWidth,
      })
    },

    zoomIn() {
      const z = this.$refs.zoomable
      z && z.zoomIn()
    },

    zoomOut() {
      const z = this.$refs.zoomable
      z && z.zoomOut()
    },

    panTo(xy) {
      const z = this.$refs.zoomable
      return z && z.panTo(xy)
    },

    setZoom(zoom) {
      const z = this.$refs.zoomable
      z && z.setZoom(zoom)
    },
  },

  inject: ['setPage'],

  components: {
    PageImage,
    PageLink,
    PageText,
    Zoomable,
  },
}
</script>
